import Big from 'big.js'

// Cover-costs fee calculation, grossed up so the beneficiary nets the full
// donation amount. Shared by both donation widgets and the Impact Hub fee-model
// donor preview so the three never drift — v7 carried a near-identical fork
// until they were merged; the only difference was `tippingEnabled`.
//
// `tippingEnabled` zeroes the internal rate: when the donor is tipping, the tip
// already covers our cut, so the cover-costs quote is processing fees only.
// Omitted (v6, Impact Hub) it's falsy and the configured internal rate applies,
// which is exactly what those callers did before.
// No config means there's no fee to quote, so this returns a bare `0` sentinel
// rather than a scaled string like every other branch. This is what BOTH
// pre-consolidation implementations returned — v6's calcFees and v7's inline fork
// — so it's the status quo, not a new choice. The scaled string was the novelty,
// and it broke completedDonation's zero-fee guard on the way through.
//
// Keeping it a number is load-bearing in one place: I18n.js's formatCurrency has
// an `alwaysDisplayFractional` branch that calls `amount.toFixed(exponent)`
// directly, which works on 0 and throws on '0.00'. No caller passes `fees` with
// that flag today, but every other branch here already returns a string, so that
// path is string-hostile and this is the one value that can satisfy it.
//
// Otherwise formatting buys nothing. The default display path is unaffected
// either way: formatCurrency runs the value through prettyAmount, whose Number()
// + toFixed(0) renders both `0` and '0.00' as "0". And it's never published —
// completedDonation reports a zero fee as null.
//
// It happens not to reach a label today either, though for different reasons per
// widget, so don't read the gates as a property of this util: v7's Tipping.jsx
// hides the cover-costs row unless `fees > 0`, while v6 has NO such gate —
// fiat/CoverCosts.jsx renders on `tipping.type` alone and formats the value
// straight into the checkbox label. What protects v6 is placement: <CoverCosts>
// sits behind `paymentOption &&`, and every resolveFeeConfig branch yields a
// present config once paymentOption is set (likewise the fees row in
// fiat/Summary.jsx, which only appears inside the PayPal backdrop).
//
// Returning 0 also keeps this branch from dereferencing `currency`, which
// callers would otherwise have to guarantee. The consolidation briefly returned
// `Big(0).toFixed(currency.exponent)` here for type uniformity; that silently
// broke completedDonation's zero-fee guard, because '0.00' is truthy.
const calcFees = (bigAmount, currency, config, tippingEnabled = false) => {
  if (!config) return 0

  const rate  = Big(config.rate)
  const base  = Big(config.base || 0).times(currency.rate)
  const cap   = config.cap && Big(config.cap).times(currency.rate)

  const internalRate  = tippingEnabled ? 0 : config.internalRate
  const amountAndBase = bigAmount.plus(base)
  const orgPayoutRate = Big('1').minus(rate).minus(internalRate)

  const rateFee       = amountAndBase.times(rate).div(orgPayoutRate).plus(base)
  const capExceeded   = cap && rateFee.gt(cap)
  const processingFee = capExceeded ? cap : rateFee
  const internalFee   = capExceeded
  ? bigAmount.plus(cap).div(Big('1').minus(internalRate))
    .minus(bigAmount).minus(cap)
  : amountAndBase.times(internalRate).div(orgPayoutRate)

  // Round separately in order to match the Ruby calculation. Do NOT collapse
  // these into one round at the end — it's the obvious tidy-up and it shifts
  // amounts by a cent against what the backend computes.
  return processingFee.round(currency.exponent)
  .plus(internalFee.round(currency.exponent)).toFixed(currency.exponent)
}

export default calcFees
